import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search as SearchIcon, ShieldCheck, Star, Clock, Wallet, Sparkles, ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CleanerCard from "@/components/CleanerCard";
import * as Icons from "lucide-react";
import { BANNER } from "@/lib/brand";

const RIBBON = ["DBS Checked", "Fully Insured", "100% Satisfaction Guarantee", "Vetted Professionals", "Secure Payments", "No Registration Fees"];
const STEPS = [
  { icon: "Search", title: "Search & Compare", text: "Enter your postcode and browse trusted, verified cleaners near you." },
  { icon: "CalendarCheck", title: "Book Instantly", text: "Pick a date and time. Payment is only taken when your cleaner accepts." },
  { icon: "Sparkles", title: "Relax & Enjoy", text: "Sit back while a professional makes your space sparkle. Leave a review." },
];
const FAQS = [
  { q: "Is there a registration fee?", a: "No. Registration is completely free for both customers and cleaners. Customers pay a small £1.99 service fee per booking." },
  { q: "When am I charged?", a: "Your payment is authorised when you book, but only captured once your cleaner accepts. If they decline, the authorisation is released automatically." },
  { q: "What's your cancellation policy?", a: "Cancel more than 24 hours before for a 100% refund, or within 24 hours for a 50% refund. If a cleaner cancels, you're fully refunded." },
  { q: "Are cleaners vetted?", a: "Yes. Cleaners can be DBS checked, insured and identity verified. Look for the verification badges on each profile." },
];

export default function Home() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [postcode, setPostcode] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch(() => {});
    api.get("/cleaners").then((r) => setCleaners(r.data.cleaners.slice(0, 3))).catch(() => {});
  }, []);

  const Icon = ({ name, ...p }) => {
    const C = Icons[name?.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Icons.Home;
    return <C {...p} />;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/28586197/pexels-photo-28586197.jpeg" alt="Clean home" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-primary/55" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-36">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="uppercase text-xs font-bold tracking-[0.2em] text-secondary">Connecting you with trusted local cleaners</span>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl font-medium tracking-tight leading-[1.05] text-background">
              A spotless home, without the search.
            </h1>
            <p className="mt-6 text-lg text-background/80 leading-relaxed max-w-xl">
              Book trusted, insured and DBS-checked cleaners near you. Free to join — pay only when your cleaner accepts.
            </p>

            <div className="mt-8 bg-white rounded-2xl p-2 flex flex-col sm:flex-row gap-2 shadow-2xl max-w-xl">
              <div className="flex items-center gap-2 flex-1 px-4 py-2">
                <SearchIcon className="h-5 w-5 text-muted-foreground" />
                <input
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/search?postcode=${postcode}`)}
                  placeholder="Enter your postcode or town"
                  className="flex-1 outline-none text-primary bg-transparent"
                  data-testid="hero-search-input"
                />
              </div>
              <button onClick={() => navigate(`/search?postcode=${postcode}`)} className="px-7 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition-colors" data-testid="hero-search-btn">
                Find Cleaners
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* RIBBON */}
      <div className="bg-primary text-background py-4 marquee">
        <div className="marquee__track">
          {[...RIBBON, ...RIBBON].map((t, i) => (
            <span key={i} className="flex items-center gap-3 text-sm font-medium uppercase tracking-wide">
              <Sparkles className="h-4 w-4 text-accent" /> {t}
            </span>
          ))}
        </div>
      </div>

      {/* BRAND BANNER */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-20">
        <div className="rounded-3xl overflow-hidden border border-primary/5 shadow-[0_8px_32px_rgba(8,90,140,0.06)]">
          <img src={BANNER} alt="From clutter to cleaner — My Clean Connect connects you with trusted local cleaners" className="w-full h-auto" loading="lazy" />
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <span className="uppercase text-xs font-bold tracking-[0.2em] text-accent">Popular Services</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">Whatever needs cleaning, we've got a pro for it.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-10">
          {services.map((s) => (
            <Link key={s.slug} to={`/search?service=${s.slug}`} className="group bg-card rounded-2xl border border-primary/5 p-6 hover-lift" data-testid={`service-${s.slug}`}>
              <span className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center mb-4 group-hover:bg-accent group-hover:text-white transition-colors">
                <Icon name={s.icon} className="h-6 w-6" />
              </span>
              <h3 className="font-display font-semibold">{s.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">from £{s.base_price_per_hour}/hr</p>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-secondary/40 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="uppercase text-xs font-bold tracking-[0.2em] text-accent">How it works</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">Booked in minutes, not days.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-card rounded-2xl p-8 border border-primary/5">
                <span className="font-display text-5xl font-medium text-accent/30">0{i + 1}</span>
                <span className="h-12 w-12 rounded-xl bg-primary text-background flex items-center justify-center my-4">
                  <Icon name={s.icon} className="h-6 w-6" />
                </span>
                <h3 className="font-display text-xl font-semibold">{s.title}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED CLEANERS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <span className="uppercase text-xs font-bold tracking-[0.2em] text-accent">Top Rated</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">Meet a few of our finest.</h2>
          </div>
          <Link to="/search" className="hidden sm:inline-flex items-center gap-2 text-accent font-medium hover:gap-3 transition-all" data-testid="view-all-cleaners">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {cleaners.map((c) => <CleanerCard key={c.cleaner_id} c={c} />)}
        </div>
      </section>

      {/* BECOME A CLEANER */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
        <div className="rounded-3xl bg-primary text-background overflow-hidden grid md:grid-cols-2">
          <div className="p-10 sm:p-14">
            <span className="uppercase text-xs font-bold tracking-[0.2em] text-secondary">Earn on your terms</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">Become a My Clean Connect™ cleaner.</h2>
            <ul className="mt-6 space-y-3 text-background/80">
              {["Free to join — no registration fees", "Set your own rates and availability", "Get paid securely after every job", "Go Premium for £7.99/mo — top of search, verified badge & zero service fees"].map((t) => (
                <li key={t} className="flex items-start gap-3"><Check className="h-5 w-5 text-accent shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
            <Link to="/register?role=cleaner" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors" data-testid="become-cleaner-btn">
              Start earning <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="relative min-h-[280px]">
            <img src="https://images.pexels.com/photos/9462636/pexels-photo-9462636.jpeg" alt="Professional cleaners" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-secondary/40 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Vetted & Insured", d: "Every cleaner can be DBS-checked and fully insured." },
            { icon: Wallet, t: "Pay on Acceptance", d: "Money only leaves your account when a cleaner confirms." },
            { icon: Star, t: "Verified Reviews", d: "Real reviews from completed, paid bookings only." },
            { icon: Clock, t: "12h Response", d: "Fast responses with automatic refunds if declined." },
          ].map((b) => (
            <div key={b.t} className="bg-card rounded-2xl p-7 border border-primary/5">
              <b.icon className="h-8 w-8 text-accent" />
              <h3 className="font-display font-semibold mt-4">{b.t}</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="text-center">
          <span className="uppercase text-xs font-bold tracking-[0.2em] text-accent">FAQ</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">Questions, answered.</h2>
        </div>
        <div className="mt-10 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-card rounded-2xl border border-primary/5 overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between px-6 py-5 text-left font-medium" data-testid={`faq-${i}`}>
                {f.q}
                <ArrowRight className={`h-4 w-4 transition-transform ${openFaq === i ? "rotate-90 text-accent" : ""}`} />
              </button>
              {openFaq === i && <p className="px-6 pb-5 text-muted-foreground leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
